import { Box, Button, Container, FormControlLabel, Grid, TextField, Typography } from '@mui/material'
import apiAppVersions from 'api/app-verisons'
import { BreadcrumbsCC, ContainerPage } from 'components/Layout'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { toastError, toastSuccess } from 'utils/helpers'
import { UploadFileHandle } from '../components'
import { clearSearchQuery } from '../store/managerVerionSlice'
import moment from 'moment'
import { TIME_FORMAT } from 'constants/common'

const CreateUpdateVersion = () => {
    const params = useParams()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { register, unregister, handleSubmit, watch, setValue, formState: { errors, isValid } } = useForm({ mode: "onChange" })
    const navigate = useNavigate()

    const [isCreatingUpdating, setIsCreatingUpdating] = useState(false)

    const [fileHandle, setFileHandle] = useState({
        exe: {
            isUploading: false,
            file: undefined,
            progress: 0,
            slot: undefined,
            isUploaded: true
        },
        deb: {
            isUploading: false,
            file: undefined,
            progress: 0,
            slot: undefined,
            isUploaded: true
        },
        dmg: {
            isUploading: false,
            file: undefined,
            progress: 0,
            slot: undefined,
            isUploaded: true
        },
        doc: {
            isUploading: false,
            file: undefined,
            progress: 0,
            slot: undefined,
            isUploaded: true
        }
    })

    const isFilesValid = () => {
        const validWin = watch("win_url") ? true : fileHandle.exe.slot
        const validMac = watch("mac_url") ? true : fileHandle.dmg.slot
        const validDeb = watch("deb_url") ? true : fileHandle.deb.slot
        return validWin && validMac && validDeb
    }

    const getVersion = async (id) => {
        try {
            const res = await apiAppVersions.get({ id: id })
            if (res?.data) {
                Object.keys(res.data).forEach(key => {
                    if (key.includes('timestamp')) return setValue(key, res.data[key] ? moment.unix(res.data[key]).format(TIME_FORMAT.FULL_24h) : '---')
                    if(key === "win_url") return setValue(key, res.data["win_file"] || res.data[key])
                    if(key === "mac_url") return setValue(key, res.data["mac_file"] || res.data[key])
                    if(key === "deb_url") return setValue(key, res.data["deb_file"] || res.data[key])
                    return setValue(key, res.data[key])
                })
                setFileHandle(prev => ({
                    exe: {
                        ...prev.exe,
                        isUploaded: res.data?.win_url ? false : true
                    },
                    dmg: {
                        ...prev.dmg,
                        isUploaded: res.data?.mac_url ? false : true
                    },
                    deb: {
                        ...prev.deb,
                        isUploaded: res.data?.deb_url ? false : true
                    },
                    doc: {
                        ...prev.doc,
                        isUploaded: res.data?.manual_url ? false : true
                    }
                }))
            } else navigate('/manager-version')
        } catch (error) {
            console.log(error)
        }
    }

    const handleUpdateCreateVersion = async (data) => {
        try {
            setIsCreatingUpdating(true)

            let res = undefined

            if (params?.versionId) {
                res = await apiAppVersions.update({
                    body: {
                        name: data.name,
                        description: data?.description,
                        win_url: data?.win_url,
                        mac_url: data?.mac_url,
                        deb_url: data?.deb_url,
                        manual_url: data?.manual_url,
                        win_file: fileHandle.exe.slot || null,
                        mac_file: fileHandle.dmg.slot || null,
                        deb_file: fileHandle.deb.slot || null,
                        manual_file: fileHandle.doc.slot || null,
                    },
                    id: params?.versionId
                })
            } else {
                res = await apiAppVersions.create({
                    body: {
                        ...data,
                        name: data.name,
                        description: data?.description,
                        win_url: data?.win_url,
                        mac_url: data?.mac_url,
                        deb_url: data?.deb_url,
                        id: params?.versionId,
                        win_file: fileHandle.exe.slot || null,
                        mac_file: fileHandle.dmg.slot || null,
                        deb_file: fileHandle.deb.slot || null,
                        manual_file: fileHandle.doc.slot || null,
                    },
                })
            }

            if (res.message === "Insert Success" || res.message === "Update success") {
                toastSuccess(params?.versionId ? t("toast.updateVersionSuccess") : t("toast.createVersionSuccess"))
                dispatch(clearSearchQuery())
                setTimeout(() => {
                    navigate('/manager-version')
                }, 250)
            }
            if (res.status === 400 && res?.detail === "version code has been used") toastError(t("toast.versionCodeExisted"))
        } catch (error) {
            if (error?.response?.data?.detail === "version code has been used") toastError(t("toast.versionCodeExisted"))
        } finally {
            setTimeout(() => {
                setIsCreatingUpdating(false)
            }, 200)
        }
    }

    useEffect(() => {
        if (params?.versionId) {
            getVersion(params?.versionId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params?.versionId])

    return (
        <ContainerPage sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: '20px' }}>
            <form onSubmit={handleSubmit(handleUpdateCreateVersion)}>
                <BreadcrumbsCC>
                    <Box display={'flex'} gap={3}>
                        <Button id='btn-cancel-edit-version' variant='outlined' onClick={() => navigate('/manager-version')}>{t('actions.cancel')}</Button>
                        <Button id='btn-update-version' disabled={isCreatingUpdating || !isValid || !isFilesValid()} type='submit'>{params?.versionId ? t('actions.update') : t('actions.add')}</Button>
                    </Box>
                </BreadcrumbsCC>
                <Typography fontWeight={'600'} mt={2} ml={2}>{t('version.detailVersion', { status: watch("status") === 1 ? t('version.active') : params?.versionId ? t('version.update') : t('version.create') })}</Typography>
                <Container>
                    <Grid container rowSpacing={2} columnSpacing={4}>
                        <Grid item xs={12} md={6}>
                            <FormControlLabel id='input-version-name' label={t('label.versionName')} labelPlacement={'top'} required control={
                                <TextField {...register("name", { required: "validate.versionNameRequired", maxLength: { value: 255, message: "validate.maxLengthText" }, setValueAs: (value) => (value ? value?.trim() : "") })} placeholder={t('placeholder.enterVersionName')} fullWidth error={!!errors.name} helperText={errors.name ? t(errors.name.message, {num: 255}) : ""} onBlur={(e) => {
                                    const trimmed = e.target.value?.trim();
                                    setValue("name", trimmed, { shouldValidate: true, shouldDirty: true });
                                }} />}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControlLabel id='input-version' label={t('label.version')} labelPlacement={'top'} required control={
                                <TextField disabled={!!params?.versionId} {...register("version_code", { required: "validate.versionCodeRequired", maxLength: { value: 100, message: "validate.maxLengthText" }, setValueAs: (value) => (value ? value?.trim() : "") })} placeholder={t('placeholder.enterVersion')} fullWidth error={!!errors.version_code} helperText={errors.version_code ? t(errors.version_code.message, {num: 100}) : ""} onBlur={(e) => {
                                    const trimmed = e.target.value?.trim();
                                    setValue("version_code", trimmed, { shouldValidate: true, shouldDirty: true });
                                }} />}
                            />
                        </Grid>
                        {params?.versionId && <Grid item xs={12} md={6}>
                            <FormControlLabel
                                id='input-version-created'
                                label={t('label.createDate')}
                                labelPlacement={'top'}
                                control={<TextField {...register("created_timestamp")} disabled={!!params?.versionId} fullWidth />}
                            />
                        </Grid>}
                        {params?.versionId && <Grid item xs={12} md={6}>
                            <FormControlLabel
                                id='input-version-reakease-date'
                                label={t('label.realeaseDate')}
                                labelPlacement={'top'}
                                control={<TextField {...register("release_timestamp")} disabled={!!params?.versionId} fullWidth />}
                            />
                        </Grid>}
                        {params?.versionId && <Grid item xs={12} md={6}>
                            <FormControlLabel
                                id='input-version-created-by'
                                label={t('label.createUser')}
                                labelPlacement={'top'}
                                control={<TextField {...register("created_by_username")} disabled={!!params?.versionId} fullWidth />}
                            />
                        </Grid>}
                        {params?.versionId && <Grid item xs={12} md={6}>
                            <FormControlLabel
                                id='input-version-realease-user'
                                label={t('label.releaseUser')}
                                labelPlacement={'top'}
                                control={<TextField {...register("released_by_username")} disabled={!!params?.versionId} fullWidth />}
                            />
                        </Grid>}
                        <Grid item xs={12}>
                            <FormControlLabel
                                id='input-version-description'
                                label={t('label.description')}
                                labelPlacement={'top'}
                                control={<TextField {...register("description", { maxLength: { value: 500, message: "validate.maxLengthText" }, setValueAs: (value) => (value ? value?.trim() : "") })} multiline minRows={4} maxRows={4} placeholder={t('placeholder.enterVersionDescription')} fullWidth error={!!errors.description} helperText={errors.description ? t(errors.description.message, {num: 500}) : ""} onBlur={(e) => {
                                    const trimmed = e.target.value?.trim();
                                    setValue("description", trimmed, { shouldValidate: true, shouldDirty: true });
                                }}/>}
                            />
                        </Grid>
                        <UploadFileHandle fileHandle={fileHandle} register={register} unregister={unregister} setFileHandle={setFileHandle} setValue={setValue} errors={errors}/>
                    </Grid>
                </Container>
            </form>
        </ContainerPage>
    )
}

export default CreateUpdateVersion